import { useAuth } from '../auth/AuthContext';

export type TeamRole = 'Admin' | 'Owner' | 'Client' | 'Member' | 'Viewer';

/**
 * Hook to check user permissions based on their role
 *
 * Role hierarchy:
 * - Owner/Client: Business owner with full access
 * - Admin: Team member with full access
 * - Member: Team member who can manage payees and timesheets
 * - Viewer: Read-only team member
 */
export function useRoleAccess() {
  const { userRole } = useAuth();

  const role = (userRole || '') as TeamRole;

  // Check if user is a business owner (has Client role or Owner role)
  const isOwner = ['Client', 'Owner'].includes(role);

  // Check if user can manage team (invite, edit, delete team members)
  const canManageTeam = ['Admin', 'Owner', 'Client'].includes(role);

  // Check if user can edit payees and timesheets
  const canEditPayees = ['Admin', 'Owner', 'Client', 'Member'].includes(role);

  // Check if user can submit timesheets
  const canSubmitTimesheets = ['Admin', 'Owner', 'Client', 'Member'].includes(role);

  // Check if user can approve/reject timesheets
  const canApproveTimesheets = ['Admin', 'Owner', 'Client'].includes(role);

  // Check if user can issue 1099 forms
  const canIssue1099 = ['Admin', 'Owner', 'Client'].includes(role);

  // Check if user can delete payees (more restrictive)
  const canDeletePayees = ['Admin', 'Owner', 'Client'].includes(role);

  // Check if user can view audit logs
  const canViewAuditLogs = ['Admin', 'Owner', 'Client'].includes(role);

  // Check if user is read-only (Viewer)
  const isViewerOnly = role === 'Viewer';

  // Check if user has a specific role
  const hasRole = (allowedRoles: TeamRole[]): boolean => {
    return allowedRoles.includes(role);
  };

  return {
    userRole: role,
    isOwner,
    canManageTeam,
    canEditPayees,
    canSubmitTimesheets,
    canApproveTimesheets,
    canIssue1099,
    canDeletePayees,
    canViewAuditLogs,
    isViewerOnly,
    hasRole,
  };
}
